import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import { TileContent, OnCallDashboardResponse } from './TileContent';
import {
  buildApiUrl,
  checkIsGlobalRefresh,
  checkIsInitialLoad,
  checkIsTileRefresh,
  executeFetch,
  hasEndpointChanged,
  resetDataForNewEndpoint,
  updateRefs,
} from './helpers';
import { RefreshIcon } from '../../assets/RefreshIcon';
import { InlineSpinner } from '../../assets/Spinner';
import { formatDateTime } from '../../utils';

interface DashboardTileProps {
  title: string;
  endpoint: string;
  refetchTrigger?: number;
  setTileAlerts: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

export const DashboardTile = ({
  title,
  endpoint,
  refetchTrigger,
  setTileAlerts,
}: DashboardTileProps) => {
  const [data, setData] = useState<OnCallDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tileRefetchTrigger, setTileRefetchTrigger] = useState(0);
  const prevRefetchTrigger = useRef<number | undefined>(undefined);
  const prevTileRefetchTrigger = useRef<number>(0);
  const prevEndpoint = useRef<string>(endpoint);
  const loadedEndpoint = useRef<string | null>(null);

  const handleTileRefresh = () => {
    setTileRefetchTrigger((prev) => prev + 1);
  };

  const lastDbFetchAt = useMemo(() => {
    return data?.last_db_fetch_at ? formatDateTime(data.last_db_fetch_at) : 'N/A';
  }, [data?.last_db_fetch_at]);

  useEffect(() => {
    // Handle endpoint changes
    if (hasEndpointChanged(endpoint, prevEndpoint)) {
      resetDataForNewEndpoint(setData, loadedEndpoint);
    }

    // Determine fetch conditions
    const isGlobalRefresh = checkIsGlobalRefresh(
      refetchTrigger,
      endpoint,
      prevRefetchTrigger,
      prevEndpoint
    );
    const isTileRefresh = checkIsTileRefresh(
      tileRefetchTrigger,
      endpoint,
      prevTileRefetchTrigger,
      prevEndpoint
    );
    const isInitialLoad = checkIsInitialLoad(endpoint, loadedEndpoint);
    const shouldFetch = isInitialLoad || isGlobalRefresh || isTileRefresh;

    // Skip fetch if no conditions are met
    if (!shouldFetch) {
      updateRefs(
        refetchTrigger,
        tileRefetchTrigger,
        endpoint,
        prevRefetchTrigger,
        prevTileRefetchTrigger,
        prevEndpoint
      );
      return;
    }

    // Prepare and execute fetch
    const url = buildApiUrl(endpoint, isGlobalRefresh, isTileRefresh);
    updateRefs(
      refetchTrigger,
      tileRefetchTrigger,
      endpoint,
      prevRefetchTrigger,
      prevTileRefetchTrigger,
      prevEndpoint
    );
    executeFetch(url, endpoint, setIsLoading, setError, setData, loadedEndpoint);
  }, [endpoint, refetchTrigger, tileRefetchTrigger]);

  useEffect(() => {
    const hasAlerts = data !== null && (data.results?.length ?? 0) > 0;
    setTileAlerts((prev) => ({ ...prev, [endpoint]: hasAlerts }));
  }, [data, endpoint, setTileAlerts]);

  return (
    <div className="flex h-[500px] flex-col rounded-lg border border-slate-200 bg-white">
      <div className="relative px-6 pt-6 pb-4">
        <h2 className="text-lg font-semibold text-slate-800">
          {data && data.results.length && endpoint !== 'conversions_by_hour_today' && '⚠️ '}
          {title}
        </h2>
        <button
          onClick={handleTileRefresh}
          className={`refresh-tile-button-${endpoint} absolute right-6 top-6 p-1.5 rounded hover:bg-slate-100 transition-colors`}
          disabled={isLoading}
        >
          <RefreshIcon className="h-4 w-4 text-slate-600" />
        </button>
        <Tooltip
          anchorSelect={`.refresh-tile-button-${endpoint}`}
          place="top"
          positionStrategy="fixed"
        >
          {`Last DB Refresh: ${lastDbFetchAt}`}
        </Tooltip>
      </div>
      <div className="custom-scrollbar min-h-[80px] flex-1 overflow-y-auto overflow-x-auto border-t border-slate-200">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <InlineSpinner />
          </div>
        )}
        {error && <p className="px-6 py-4 text-sm text-red-500">{error}</p>}
        {!isLoading && !error && data !== null && (
          <div className="rounded">
            <TileContent data={data} />
          </div>
        )}
      </div>
    </div>
  );
};
